import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import PageLayout from '../../components/feature/PageLayout';
import PageHero from '../../components/feature/PageHero';
import PageSectionNav from '../../components/feature/PageSectionNav';
import {
  type SeasonalArticle,
  type EventType,
  getArticlesCyclic,
  getCurrentEvent,
  getNextEvent,
  getDaysUntil,
  getDaysRemaining,
} from '@/data/copticCalendar';

const INSTAGRAM_HANDLE = 'smpcvi';
const localNewsGallery = [
  {
    src: '/assets/readdy/9eda875d-3d3d-4284-9ae9-bca767912453_IMG_0496-2-67e8e63d7d3e.jpeg',
    alt: 'Church sanctuary',
  },
  {
    src: '/assets/readdy/442a8abb-782f-432f-b6c4-09b7eb30eb08_IMG_0444-eafb70ea2d69.jpeg',
    alt: 'Church gathering',
  },
  {
    src: '/assets/readdy/55eb7943-6622-4ed4-a6b9-5e1f70851100_IMG_0507-e9374c281525.jpeg',
    alt: 'Church exterior',
  },
  {
    src: '/assets/readdy/a819e154-7847-4c75-899f-b7f02f0a9915_IMG_0518-0c77e3583d1e.jpeg',
    alt: 'Church community',
  },
  {
    src: '/assets/readdy/acf5d81a-7621-4035-b51f-5b78fc09c87e_IMG_0509-af3d7b64dee9.jpeg',
    alt: 'Church service',
  },
  {
    src: '/assets/readdy/ae412476-d8a4-4171-9f16-e652ac1aab98_IMG_0482-782cd519e7c7.jpeg',
    alt: 'Church event',
  },
];

const TYPE_STYLE: Record<EventType, {
  badgeBg: string;
  badgeText: string;
  accent: string;
  border: string;
  bg: string;
  dot: string;
}> = {
  feast: {
    badgeBg: 'bg-amber-50 border-amber-300',
    badgeText: 'text-amber-800',
    accent: 'text-amber-600',
    border: 'border-amber-200',
    bg: 'bg-amber-50',
    dot: 'bg-amber-400',
  },
  fast: {
    badgeBg: 'bg-sky-50 border-sky-300',
    badgeText: 'text-sky-800',
    accent: 'text-sky-600',
    border: 'border-sky-200',
    bg: 'bg-sky-50',
    dot: 'bg-sky-400',
  },
  'holy-week': {
    badgeBg: 'bg-violet-50 border-violet-300',
    badgeText: 'text-violet-800',
    accent: 'text-violet-600',
    border: 'border-violet-200',
    bg: 'bg-violet-50',
    dot: 'bg-violet-500',
  },
  season: {
    badgeBg: 'bg-rose-50 border-rose-300',
    badgeText: 'text-rose-800',
    accent: 'text-rose-600',
    border: 'border-rose-200',
    bg: 'bg-rose-50',
    dot: 'bg-rose-400',
  },
};

// ── COUNTDOWN BANNER ──
function LiturgicalBanner({ articles }: { articles: SeasonalArticle[] }) {
  const { t } = useTranslation();
  const currentEvent = useMemo(() => getCurrentEvent(), []);
  const nextEvent = useMemo(() => getNextEvent(), []);

  if (!currentEvent && !nextEvent) return null;

  return (
    <div className="mb-10">
      <div className="bg-gradient-to-r from-[#3d1a1e] via-[#5a252c] to-[#3d1a1e] rounded-2xl p-6 md:p-8 text-white">
        <div className="flex flex-col lg:flex-row items-start lg:items-center gap-6">
          {/* Current Season */}
          {currentEvent && (
            <div className="flex-1">
              <p className="text-amber-400 text-xs uppercase tracking-wider font-semibold mb-1">
                {currentEvent.type === 'fast' ? t('newsPage.currentlyObserving') : t('newsPage.currentSeason')}
              </p>
              <h3 className="text-xl md:text-2xl font-serif font-bold text-white mb-1" style={{ fontFamily: '"Crimson Text", serif' }}>
                {currentEvent.name}
              </h3>
              <p className="text-white/70 text-sm">
                <i className="ri-calendar-line mr-1.5"></i>
                {currentEvent.dateDisplay}
                {currentEvent.endDate && (
                  <span className="ml-2 text-amber-400 font-semibold">
                    ({getDaysRemaining(currentEvent)} days remaining)
                  </span>
                )}
              </p>
            </div>
          )}

          {/* Divider */}
          {currentEvent && nextEvent && (
            <div className="hidden lg:block w-px h-12 bg-white/20"></div>
          )}

          {/* Countdown to Next */}
          {nextEvent && (
            <div className="flex-shrink-0">
              <p className="text-amber-400 text-xs uppercase tracking-wider font-semibold mb-1">
                {nextEvent.type === 'feast' ? t('newsPage.nextFeast') : t('newsPage.nextFast')}
              </p>
              <div className="flex items-center gap-4">
                <div className="text-center">
                  <span className="block text-3xl md:text-4xl font-bold text-amber-400">{getDaysUntil(nextEvent)}</span>
                  <span className="block text-xs text-white/60 uppercase">{t('newsPage.days')}</span>
                </div>
                <div>
                  <p className="text-lg md:text-xl font-serif font-bold text-white" style={{ fontFamily: '"Crimson Text", serif' }}>
                    {nextEvent.name}
                  </p>
                  <p className="text-white/70 text-sm">
                    <i className="ri-calendar-event-line mr-1.5"></i>
                    {nextEvent.dateDisplay}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── ARTICLE CARD ──
function ArticleCard({ article, onClick }: { article: SeasonalArticle; onClick: () => void }) {
  const { t } = useTranslation();
  const s = TYPE_STYLE[article.type];
  const currentEvent = useMemo(() => getCurrentEvent(), []);
  const isCurrentSeason = currentEvent?.id === article.eventId;

  return (
    <article
      onClick={onClick}
      className="group bg-white rounded-2xl overflow-hidden border border-stone-100 hover:border-stone-200 transition-all cursor-pointer relative"
    >
      {isCurrentSeason && (
        <span className="absolute top-3 left-3 z-10 inline-flex items-center gap-1 px-2.5 py-1 bg-[#3d1a1e]/90 text-white text-xs font-semibold rounded-full">
          <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse"></span>
          {t('newsPage.now')}
        </span>
      )}
      <div className="h-52 w-full overflow-hidden">
        <img
          src={article.image}
          alt={article.imageAlt}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
      </div>
      <div className="p-6">
        <div className="flex items-center gap-3 mb-3">
          <span className={`inline-flex items-center px-3 py-1 rounded-full border text-xs font-bold uppercase tracking-wider ${s.badgeBg} ${s.badgeText}`}>
            {article.badge}
          </span>
          <span className="text-xs text-stone-400 flex items-center gap-1">
            <i className="ri-calendar-line"></i>
            {article.dateRange}
          </span>
        </div>
        {article.copticTitle && (
          <p className={`font-serif italic text-sm mb-1 ${s.accent}`} style={{ fontFamily: '"Crimson Text", serif' }}>
            {article.copticTitle}
          </p>
        )}
        <h3 className="font-serif font-bold text-stone-800 text-xl mb-2 group-hover:text-amber-700 transition-colors" style={{ fontFamily: '"Crimson Text", serif' }}>
          {article.title}
        </h3>
        <p className="text-stone-500 text-sm leading-relaxed line-clamp-3 mb-4">{article.excerpt}</p>
        {(article.type === 'fast' || article.type === 'holy-week') && article.fastingRestrictions && (
          <div className="flex items-center gap-1.5 mb-3 text-xs text-stone-400">
            <i className="ri-cup-line"></i>
            <span>{t('newsPage.fastingObserved')}</span>
          </div>
        )}
        <span className={`inline-flex items-center gap-1.5 text-sm font-medium ${s.accent}`}>
          {t('newsPage.readMore')} <i className="ri-arrow-right-line"></i>
        </span>
      </div>
    </article>
  );
}

// ── ARTICLE DETAIL ──
function ArticleDetail({ article, onBack }: { article: SeasonalArticle; onBack: () => void }) {
  const { t } = useTranslation();
  const s = TYPE_STYLE[article.type];
  return (
    <div className="max-w-3xl mx-auto">
      <button
        onClick={onBack}
        className="inline-flex items-center gap-2 text-sm text-stone-500 hover:text-stone-800 transition-colors cursor-pointer mb-8"
      >
        <i className="ri-arrow-left-line"></i>
        {t('newsPage.backToArticles')}
      </button>

      <div className="w-full h-72 sm:h-96 rounded-2xl overflow-hidden mb-8">
        <img src={article.image} alt={article.imageAlt} className="w-full h-full object-cover object-top" />
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <span className={`inline-flex items-center px-3 py-1 rounded-full border text-xs font-bold uppercase tracking-wider ${s.badgeBg} ${s.badgeText}`}>
          {article.badge}
        </span>
        <span className="text-xs text-stone-400 flex items-center gap-1.5">
          <i className="ri-calendar-line"></i>
          {article.dateRange}
        </span>
      </div>

      {article.copticTitle && (
        <p className={`font-serif italic text-lg sm:text-xl mb-2 ${s.accent}`} style={{ fontFamily: '"Crimson Text", serif' }}>
          {article.copticTitle}
        </p>
      )}

      <h1 className="font-serif font-bold text-stone-900 text-3xl sm:text-4xl leading-tight mb-6" style={{ fontFamily: '"Crimson Text", serif' }}>
        {article.title}
      </h1>

      <div className="flex items-center gap-3 mb-8">
        <div className={`h-px flex-1 ${s.dot} opacity-20`}></div>
        <div className={`w-2 h-2 rounded-full ${s.dot} opacity-40`}></div>
        <div className={`h-px flex-1 ${s.dot} opacity-20`}></div>
      </div>

      <p className={`text-base sm:text-lg font-medium leading-relaxed mb-8 ${s.accent}`} style={{ fontFamily: '"Crimson Text", serif' }}>
        {article.excerpt}
      </p>

      {article.fastingRestrictions && (
        <div className={`mb-8 p-5 rounded-xl border ${s.border} ${s.bg}`}>
          <div className="flex items-start gap-3">
            <div className={`w-8 h-8 flex items-center justify-center rounded-full bg-white flex-shrink-0 mt-0.5`}>
              <i className={`ri-cup-line text-lg ${s.accent}`}></i>
            </div>
            <div>
              <p className="text-sm font-semibold text-stone-800 mb-1">{t('newsPage.fastingGuidelines')}</p>
              <p className="text-sm text-stone-600 leading-relaxed">{article.fastingRestrictions}</p>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-5">
        {article.body.map((para, i) => (
          <p key={i} className="text-stone-600 text-base leading-relaxed">
            {para}
          </p>
        ))}
      </div>

      <div className={`mt-10 pt-6 border-t ${s.border} flex items-center gap-3`}>
        <div className={`w-8 h-8 rounded-full ${s.dot} opacity-30 flex items-center justify-center`}>
          <i className="ri-quill-pen-line text-white text-xs"></i>
        </div>
        <p className="text-sm text-stone-500">{article.author}</p>
      </div>

      <button
        onClick={onBack}
        className="mt-10 inline-flex items-center gap-2 text-sm text-stone-500 hover:text-stone-800 transition-colors cursor-pointer"
      >
        <i className="ri-arrow-left-line"></i>
        {t('newsPage.backToArticles')}
      </button>
    </div>
  );
}

// ── NEWS PAGE ──
export default function NewsPage() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedArticle, setSelectedArticle] = useState<SeasonalArticle | null>(null);

  // Dynamically get articles that are currently active or upcoming
  const articles = useMemo(() => getArticlesCyclic(), []);

  // Deep-link: open article from ?article=eventId param
  useEffect(() => {
    const articleId = searchParams.get('article');
    if (articleId && articles.length > 0) {
      const found = articles.find(a => a.eventId === articleId);
      if (found) {
        setSelectedArticle(found);
      }
    }
  }, [searchParams, articles]);

  useEffect(() => {
    if (selectedArticle) window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [selectedArticle]);

  const handleSelectArticle = (article: SeasonalArticle) => {
    setSelectedArticle(article);
    setSearchParams({ article: article.eventId });
  };

  const handleBack = () => {
    setSelectedArticle(null);
    setSearchParams({});
  };

  const newsletters = [
    {
      id: 1,
      title: t('newsPage.newsletter1Title'),
      date: 'January 2025',
      description: t('newsPage.newsletter1Desc'),
      image: '/assets/readdy/5b2ce4067850f11889f7445dd031c400-4ee2c140aa40.png',
    },
    {
      id: 2,
      title: t('newsPage.newsletter2Title'),
      date: 'December 2024',
      description: t('newsPage.newsletter2Desc'),
      image: '/assets/readdy/5b2ce4067850f11889f7445dd031c400-4ee2c140aa40.png',
    },
    {
      id: 3,
      title: t('newsPage.newsletter3Title'),
      date: 'November 2024',
      description: t('newsPage.newsletter3Desc'),
      image: '/assets/readdy/5b2ce4067850f11889f7445dd031c400-4ee2c140aa40.png',
    },
    {
      id: 4,
      title: t('newsPage.newsletter4Title'),
      date: 'October 2024',
      description: t('newsPage.newsletter4Desc'),
      image: '/assets/readdy/5b2ce4067850f11889f7445dd031c400-4ee2c140aa40.png',
    },
  ];

  return (
    <PageLayout hasHero>
      <PageHero
        title={t('newsPage.title')}
        subtitle={t('newsPage.subtitle')}
        backgroundImage="/assets/readdy/ae412476-d8a4-4171-9f16-e652ac1aab98_IMG_0482-782cd519e7c7.jpeg"
      />

      <PageSectionNav
        sections={[
          { id: 'articles', label: t('newsPage.articles') },
          { id: 'posts', label: t('newsPage.posts') },
          { id: 'newsletters', label: t('newsPage.newsletters') },
        ]}
      />

      {/* ── ARTICLES SECTION ── */}
      <section id="articles" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1.5 bg-[#3d1a1e]/10 text-[#3d1a1e] text-sm font-medium rounded-full mb-4">
              {t('newsPage.seasonalPosts')}
            </span>
            <h2 className="text-3xl font-serif font-bold text-stone-900 mb-3" style={{ fontFamily: '"Crimson Text", serif' }}>
              {t('newsPage.events')}
            </h2>
            <p className="text-stone-500 max-w-xl mx-auto text-sm">
              {t('newsPage.seasonalPostsDesc')}
            </p>
          </div>

          {!selectedArticle && <LiturgicalBanner articles={articles} />}

          {selectedArticle ? (
            <ArticleDetail article={selectedArticle} onBack={handleBack} />
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {articles.map((article) => (
                <ArticleCard key={article.eventId} article={article} onClick={() => handleSelectArticle(article)} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ── POSTS SECTION ── */}
      <section id="posts" className="py-20 bg-stone-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1.5 bg-[#d4a017]/20 text-[#8b6914] text-sm font-medium rounded-full mb-4">
              {t('newsPage.socialMedia')}
            </span>
            <h2 className="text-3xl font-serif font-bold text-stone-900 mb-3" style={{ fontFamily: '"Crimson Text", serif' }}>
              {t('newsPage.posts')}
            </h2>
            <p className="text-stone-500 max-w-xl mx-auto text-sm mb-6">
              {t('newsPage.socialMediaDesc')}
            </p>
            <a
              href={`https://www.instagram.com/${INSTAGRAM_HANDLE}/`}
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-white font-medium text-sm transition-all cursor-pointer bg-gradient-to-r from-amber-500 via-rose-500 to-[#3d1a1e] hover:opacity-90 whitespace-nowrap"
            >
              <i className="ri-instagram-line"></i>
              @{INSTAGRAM_HANDLE}
            </a>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
            {localNewsGallery.map((item) => (
              <a
                key={item.src}
                href={`https://www.instagram.com/${INSTAGRAM_HANDLE}/`}
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="group block aspect-square overflow-hidden rounded-xl border border-stone-200 bg-white shadow-sm cursor-pointer"
              >
                <img
                  src={item.src}
                  alt={item.alt}
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </a>
            ))}
          </div>

          <div className="mt-10 text-center">
            <a
              href={`https://www.instagram.com/${INSTAGRAM_HANDLE}/`}
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="inline-flex items-center gap-2 px-8 py-3 border-2 border-[#3d1a1e] text-[#3d1a1e] font-medium rounded-full hover:bg-[#3d1a1e] hover:text-white transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-instagram-line"></i>
              {t('newsPage.viewAllOnInstagram')}
            </a>
          </div>
        </div>
      </section>

      {/* ── NEWSLETTERS SECTION ── */}
      <section id="newsletters" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1.5 bg-[#3d1a1e]/10 text-[#3d1a1e] text-sm font-medium rounded-full mb-4">
              {t('newsPage.monthlyUpdates')}
            </span>
            <h2 className="text-3xl font-serif font-bold text-stone-900 mb-3" style={{ fontFamily: '"Crimson Text", serif' }}>
              {t('newsPage.monthlyNewsletters')}
            </h2>
            <p className="text-stone-500 max-w-xl mx-auto text-sm">
              {t('newsPage.newslettersDesc')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {newsletters.map((newsletter) => (
              <div key={newsletter.id} className="bg-white rounded-2xl overflow-hidden border border-stone-100 hover:border-stone-200 transition-all group cursor-pointer">
                <div className="h-56 w-full overflow-hidden bg-stone-100">
                  <img
                    src={newsletter.image}
                    alt={newsletter.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-5">
                  <p className="text-xs text-amber-600 font-medium mb-2">{newsletter.date}</p>
                  <h3 className="text-lg font-bold text-stone-900 mb-2 group-hover:text-[#3d1a1e] transition-colors">{newsletter.title}</h3>
                  <p className="text-sm text-stone-600 mb-4 line-clamp-2">{newsletter.description}</p>
                  <a href="#" className="inline-flex items-center gap-2 text-sm font-medium text-amber-600 hover:text-amber-700 whitespace-nowrap">
                    <i className="ri-download-line"></i>
                    {t('newsPage.downloadPDF')}
                  </a>
                </div>
              </div>
            ))}
          </div>

          {/* Subscribe Box */}
          <div className="mt-12 bg-stone-50 rounded-2xl p-8 border border-stone-200">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div>
                <h4 className="text-xl font-bold text-stone-900 mb-2">{t('newsPage.subscribeNewsletter')}</h4>
                <p className="text-stone-600">{t('newsPage.subscribeDesc')}</p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
                <input
                  type="email"
                  placeholder={t('newsPage.emailPlaceholder')}
                  className="flex-1 md:w-64 px-4 py-3 border border-stone-200 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                />
                <button className="px-6 py-3 bg-[#3d1a1e] text-white font-medium rounded-full hover:bg-[#2a1215] transition-colors whitespace-nowrap cursor-pointer">
                  {t('newsPage.subscribe')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* View Full Calendar CTA */}
      <section className="py-20 bg-gradient-to-br from-[#3d1a1e] to-[#2a1215]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="w-16 h-16 flex items-center justify-center bg-white/10 rounded-full mx-auto mb-6">
            <i className="ri-calendar-line text-3xl text-amber-400"></i>
          </div>
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-white mb-4">{t('home.viewFullCalendar')}</h2>
          <p className="text-lg text-white/80 mb-8 max-w-2xl mx-auto">
            {t('newsPage.exploreCalendar')}
          </p>
          <a
            href="/calendar"
            className="inline-flex items-center gap-2 px-8 py-3.5 bg-amber-500 text-[#3d1a1e] font-semibold rounded-full hover:bg-amber-400 transition-all hover:scale-105 cursor-pointer whitespace-nowrap"
          >
            <span>{t('calendarPage.goToCalendar')}</span>
            <i className="ri-arrow-right-line"></i>
          </a>
        </div>
      </section>
    </PageLayout>
  );
}
